import { getRuntimeConfig } from '../config/runtimeConfig';
const scriptUrl = 'https://accounts.google.com/gsi/client';

function getState() {
  if (!window.__importErpGis) window.__importErpGis = {};
  return window.__importErpGis;
}

export function loadGoogleIdentity() {
  const state = getState();
  const clientId = getRuntimeConfig().GOOGLE_CLIENT_ID;
  if (!clientId) return Promise.reject(new Error('Google sign-in is not configured. Please contact your administrator.'));
  if (state.clientId && state.clientId !== clientId) {
    return Promise.reject(new Error('Google sign-in configuration changed. Please reload the page.'));
  }
  if (state.ready) return state.ready;
  const scriptReady = window.google?.accounts?.oauth2
    ? Promise.resolve(window.google.accounts.oauth2)
    : new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${scriptUrl}"]`);
      const created = !script;
      if (created) {
        script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
      }
      const cleanup = () => {
        clearTimeout(timer);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
      };
      const failed = () => {
        cleanup();
        if (created) script.remove();
        reject(new Error('Unable to load Google sign-in. Please check your connection and try again.'));
      };
      const loaded = () => {
        if (!window.google?.accounts?.oauth2) return failed();
        cleanup();
        resolve(window.google.accounts.oauth2);
      };
      const timer = setTimeout(failed, 15000);
      script.addEventListener('load', loaded);
      script.addEventListener('error', failed);
      if (created) document.head.appendChild(script);
    });
  state.ready = scriptReady.then(identity => {
    state.oauth2 = identity;
    state.clientId = clientId;
    return identity;
  }).catch(error => {
    state.ready = null;
    throw error;
  });
  return state.ready;
}

export function requestGoogleCode(signal) {
  const state = getState();
  if (!state.oauth2) {
    loadGoogleIdentity().catch(() => {});
    return Promise.reject(new Error('Google sign-in is still loading. Please try clicking again in a moment.'));
  }
  if (state.clientId !== getRuntimeConfig().GOOGLE_CLIENT_ID) return Promise.reject(new Error('Google sign-in configuration changed. Please reload the page.'));
  if (signal.aborted) return Promise.reject(new Error('Google sign-in was cancelled.'));
  if (state.activeAttempt) return Promise.reject(new Error('Google sign-in is already in progress.'));
  return new Promise((resolve, reject) => {
    let finished = false;
    const finish = (code, error) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      state.activeAttempt = null;
      if (error) reject(new Error(error));
      else resolve(code);
    };
    const abort = () => finish(null, 'Google sign-in was cancelled.');
    const timer = setTimeout(() => finish(null, 'Google sign-in timed out. Please try again.'), 300000);
    signal.addEventListener('abort', abort, { once: true });
    state.activeAttempt = finish;
    try {
      const client = state.oauth2.initCodeClient({
        client_id: state.clientId,
        scope: 'openid email profile',
        include_granted_scopes: false,
        ux_mode: 'popup',
        select_account: true,
        callback: response => {
          if (response?.error) finish(null, response.error === 'access_denied'
            ? 'Google sign-in was cancelled.' : 'Google authentication failed. Please try again.');
          else finish(response?.code, response?.code ? null : 'Google did not return an authorization code. Please try again.');
        },
        error_callback: error => finish(null, error?.type === 'popup_closed'
          ? 'Google sign-in was cancelled.' : error?.type === 'popup_failed_to_open'
            ? 'Allow popups for this site and try Google sign-in again.' : 'Google sign-in is unavailable. Please try again.'),
      });
      client.requestCode();
    } catch {
      finish(null, 'Google sign-in is unavailable. Please try again.');
    }
  });
}
